<template>
    <div class="map-component">
        <ejs-maps id="listing-map"
                  style="height:500px;width:100%;"
                  :centerPosition="centerPosition"
                  :zoomSettings="zoomSettings"
                  :markerClick="handleMarkerClick">
            <e-maps-layers>
                <e-maps-layer :urlTemplate="'https://tile.openstreetmap.org/level/tileX/tileY.png'" :markerSettings='visibleMarkers'></e-maps-layer>
            </e-maps-layers>
        </ejs-maps>

        <!-- Marker Detail Modal Overlay -->
        <div v-if="selectedMarker"
             class="marker-detail-overlay"
             @click.self="closeMarkerDetail"
             @keydown.esc="closeMarkerDetail"
             tabindex="0">
            <div class="marker-detail-card">
                <!-- Close button -->
                <button
                    @click="closeMarkerDetail"
                    class="btn-close"
                    aria-label="Close">
                </button>

                <!-- Header -->
                <div class="marker-detail-header">
                    <h5 class="mb-1">{{ selectedMarker.address }}</h5>
                    <p class="text-muted mb-0">
                        <i class="far fa-clock me-1"></i>
                        {{ selectedMarker.schedule }}
                    </p>
                </div>

                <!-- Divider -->
                <hr class="my-3" />

                <!-- Action Buttons - Hidden initially, revealed after 750ms delay -->
                <div v-if="buttonsEnabled" class="marker-detail-actions">
                    <!-- AUTHENTICATED: Save/Unsave button -->
                    <button
                        v-if="isAuthenticated && selectedMarker.ownershipState !== 'Owned'"
                        @click="toggleListingSave"
                        :disabled="isSavingMarker"
                        :class="{ 'btn-loading': isSavingMarker }"
                        class="btn btn-outline-primary action-btn-full">
                        <span v-if="isSavingMarker" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <i v-else :class="getMarkerHeartIcon()" class="me-2"></i>
                        <span>{{ getMarkerSaveText() }}</span>
                    </button>

                    <!-- AUTHENTICATED: Owned listing (read-only) -->
                    <button
                        v-if="isAuthenticated && selectedMarker.ownershipState === 'Owned'"
                        disabled
                        class="btn btn-outline-success action-btn-full">
                        <i class="fa fa-check me-2"></i>
                        <span>Your Listing</span>
                    </button>

                    <!-- ANONYMOUS: Sign up prompt -->
                    <a
                        v-if="!isAuthenticated"
                        :href="getSignupUrl()"
                        class="btn btn-primary action-btn-full">
                        <i class="far fa-heart me-2"></i>
                        <span>Login to Save and Get Directions</span>
                    </a>

                    <!-- Details button -->
                    <a :href="selectedMarker.url"
                       class="btn btn-outline-secondary action-btn-full">
                        <i class="fas fa-info-circle me-2"></i>
                        <span>View Details</span>
                    </a>

                    <!-- Directions button -->
                    <a v-if="isAuthenticated"
                        :href="getDirectionsUrl()"
                        target="_blank"
                        class="btn btn-outline-secondary action-btn-full">
                        <i class="fas fa-route me-2"></i>
                        <span>Get Directions</span>
                    </a>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
    import { MapsComponent, LayersDirective, LayerDirective, MarkersDirective, MarkerDirective, MapsTooltip, Marker, Zoom } from '@syncfusion/ej2-vue-maps';
    import { useListingSaving } from '@shared/composables/useListingSaving';

    /**
     * ListingsMap Component
     *
     * Displays an interactive map with listing markers using Syncfusion Maps.
     *
     * Features:
     * - Click markers to open detail modal
     * - Save/unsave listings (authenticated users)
     * - Signup prompt for anonymous users
     * - View details and get directions
     * - Automatic marker icon updates based on saved state
     * - Keyboard accessible (ESC key closes modal)
     *
     * Events:
     * - @listing-saved: Emitted when user saves/unsaves a listing
     *   Payload: { listingId, oldState, newState }
     *
     * - @listing-unfavorited: Emitted when user removes from saved (legacy)
     *
     * Props:
     * - markers: Array of marker groups with data sources
     * - selectedGroups: Set of active group IDs to display
     * - centerLat/centerLng: Map center coordinates
     * - isAuthenticated: Boolean indicating user auth status
     *
     * Dependencies:
     * - @syncfusion/ej2-vue-maps
     * - useListingSaving composable
     */
    export default {
        name: 'ListingsMap',
        emits: ['listing-unfavorited', 'listing-saved'],

        setup() {
            const { isSaving, toggleSaved } = useListingSaving();
            return {
                isSaving,
                toggleSaved
            };
        },

        components: {
            'ejs-maps': MapsComponent,
            'e-maps-layers': LayersDirective,
            'e-maps-layer': LayerDirective,
            'e-layersettings-markers': MarkersDirective,
            'e-layersettings-marker': MarkerDirective
        },
        data() {
            return {
                zoomSettings: {
                    enable: true,
                    zoomFactor: 13,
                    minZoom: 12,
                    maxZoom: 17,
                    toolbarSettings: {
                        borderWidth: 3,
                        borderColor: 'black',
                        buttonSettings: {
                            toolbarItems: ['ZoomIn', 'ZoomOut']
                        }
                    }

                },
                //tooltipSettings: {
                //    visible: true,
                //    duration: 0,
                //    shape: 'Image',
                //    imageUrlValuePath: 'iconUrl',
                //    format: '${Title}',
                //    template: '#markerTooltipTemplate',
                //    textStyle: {
                //        fontFamily: 'inherit'
                //    }
                //},
                userLocation: null,
                USER_LOCATION_STORAGE_KEY: 'garagr_user_location',
                selectedMarker: null,
                isSavingMarker: false,
                markerClickDebounce: false,
                buttonsEnabled: true,
                buttonEnableTimeout: null
            }
        },
        props: {
            showDetailsLink: {
                type: Boolean,
                required: false,
                default: true
            },
            markers: {
                type: Array,
                required: true
            },
            selectedGroups: {
                type: Set,
                required: true
            },
            centerLat: {
                type: Number,
                default: 26.224472
            },
            centerLng: {
                type: Number,
                default: -98.230002
            },
            isAuthenticated: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        provide: {
            maps: [MapsTooltip, Marker, Zoom]
        },
        computed: {
            visibleMarkers() {
                const markers = [];
                // Add user location marker if available
                if (this.userLocation) {
                    markers.push({
                        groupId: 'user-location',
                        name: 'Your Location',
                        width: 12,
                        height: 12,
                        visible: true,
                        shape: 'Circle',
                        fill: '#ff0000',
                        border: {
                            width: 2,
                            color: '#ffffff'
                        },
                        dataSource: [{
                            id: 'user-location',
                            title: 'Your Location',
                            address: 'Your current location',
                            latitude: this.userLocation.lat,
                            longitude: this.userLocation.lng,
                            schedule: '',
                            ownershipState: 'User',
                            url: '#',
                            groupId: 'user-location'
                        }],
                        tooltipSettings: {
                            visible: false
                        },
                        animationDuration: 0
                    });
                }

                // Filter and return markers for selected groups only, with tooltips disabled
                const regularMarkers = this.markers
                    .filter(markerGroup =>
                        this.selectedGroups.has(markerGroup.groupId?.toString())
                    )
                    .map(markerGroup => ({
                        ...markerGroup,
                        tooltipSettings: {
                            visible: false
                        }
                    }));

                return [...markers, ...regularMarkers];
            },
            centerPosition() {
                // Use user location for center if available, otherwise use provided center
                if (this.userLocation) {
                    return {
                        latitude: this.userLocation.lat,
                        longitude: this.userLocation.lng
                    };
                }
                return {
                    latitude: this.centerLat,
                    longitude: this.centerLng
                };
            },
            handleCustomEvent() {
                // Custom event handler
            }
        },
        mounted() {
            this.$el.addEventListener('update-listings', this.handleCustomEvent);
            const templateScript = this.showDetailsLink ? document.getElementById('markerTooltipTemplate') : document.getElementById("detailsMarkerTooltipTemplate");
            
            if (templateScript) {
                // this.tooltipSettings.template = templateScript.innerHTML;
            }

            // Load user location from localStorage
            this.loadUserLocation();
        },
        methods: {
            loadUserLocation() {
                try {
                    const saved = localStorage.getItem(this.USER_LOCATION_STORAGE_KEY);
                    if (saved) {
                        const locationData = JSON.parse(saved);
                        // Validate the location data structure
                        if (locationData.lat && locationData.lng && locationData.timestamp) {
                            this.userLocation = locationData;
                        }
                    }
                } catch (error) {
                    console.error('Error loading user location:', error);
                    this.userLocation = null;
                }
            },

            updateUserLocation(locationData) {
                this.userLocation = locationData;
                // The computed properties will automatically update the map
                // No need to manually refresh as Vue reactivity handles it
            },

            clearUserLocation() {
                this.userLocation = null;
                // The computed properties will automatically update the map
            },


            showNotification(message, type = 'info') {
                // Create notification element
                const notification = document.createElement('div');
                notification.className = `notification notification-${type}`;
                notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: ${type === 'error' ? '#dc3545' : '#28a745'};
                    color: white;
                    padding: 10px 20px;
                    border-radius: 5px;
                    z-index: 9999;
                    font-size: 14px;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                `;
                notification.textContent = message;

                document.body.appendChild(notification);
                
                // Show notification
                setTimeout(() => {
                    notification.style.opacity = '1';
                }, 100);

                // Hide and remove notification
                setTimeout(() => {
                    notification.style.opacity = '0';
                    setTimeout(() => {
                        if (notification.parentNode) {
                            notification.parentNode.removeChild(notification);
                        }
                    }, 300);
                }, 3000);
            },

            handleMarkerClick(args) {
                // args.data contains the marker data
                // Ignore clicks on user location marker
                if (args.data.id === 'user-location') {
                    return;
                }

                // Prevent rapid clicks
                if (this.markerClickDebounce) {
                    return;
                }

                this.markerClickDebounce = true;
                setTimeout(() => {
                    this.markerClickDebounce = false;
                }, 300);

                this.selectedMarker = {
                    id: args.data.id,
                    title: args.data.title,
                    address: args.data.address,
                    latitude: args.data.latitude,
                    longitude: args.data.longitude,
                    schedule: args.data.schedule,
                    url: args.data.url,
                    ownershipState: args.data.state,
                    groupId: args.data.groupId
                };

                // Disable buttons initially to prevent accidental clicks
                this.buttonsEnabled = false;

                // Enable buttons after 750ms delay
                this.buttonEnableTimeout = setTimeout(() => {
                    this.buttonsEnabled = true;
                }, 750);

                // Focus overlay on next tick for keyboard accessibility
                this.$nextTick(() => {
                    const overlay = document.querySelector('.marker-detail-overlay');
                    if (overlay) {
                        overlay.focus();
                    }
                });
            },

            closeMarkerDetail() {
                // Clear the button enable timeout if it's still pending
                if (this.buttonEnableTimeout) {
                    clearTimeout(this.buttonEnableTimeout);
                    this.buttonEnableTimeout = null;
                }
                
                this.selectedMarker = null;
                this.buttonsEnabled = true; // Reset for next time
            },

            getMarkerHeartIcon() {
                if (this.isSavingMarker) {
                    return 'fa fa-spinner fa-spin';
                }

                if (this.selectedMarker?.ownershipState === 'Saved') {
                    return 'fas fa-heart';
                }

                return 'far fa-heart';
            },

            getMarkerSaveText() {
                if (this.isSavingMarker) {
                    return 'Saving...';
                }

                return this.selectedMarker?.ownershipState === 'Saved'
                    ? 'Saved'
                    : 'Save This Listing';
            },

            getDirectionsUrl() {
                if (!this.selectedMarker) return '#';

                // Use coordinates for more reliable directions
                const lat = this.selectedMarker.latitude;
                const lng = this.selectedMarker.longitude;

                // Google Maps directions URL format: directions to coordinates
                return `https://www.google.com/maps/dir//${lat},${lng}`;
            },

            getSignupUrl() {
                if (!this.selectedMarker) {
                    return '/identity/account/register?returnUrl=/listings';
                }

                // Extract listing ID from URL
                // Expected format: "listings/{id}/{slug}" or "/listings/{id}/{slug}"
                try {
                    const urlParts = this.selectedMarker.url.split('/').filter(part => part);
                    
                    // Validate URL structure and extract ID
                    if (urlParts.length >= 2 && urlParts[0] === 'listings') {
                        const listingId = urlParts[1];
                        
                        if (listingId) {
                            // Build return URL to listing detail page
                            const returnUrl = encodeURIComponent(`/listings/${listingId}`);
                            return `/identity/account/register?returnUrl=${returnUrl}`;
                        }
                    }
                    
                    console.warn('Unexpected listing URL format:', this.selectedMarker.url);
                } catch (error) {
                    console.error('Error parsing listing URL:', this.selectedMarker.url, error);
                }

                // Fallback to general listings page
                return '/identity/account/register?returnUrl=/listings';
            },

            async toggleListingSave() {
                if (this.isSavingMarker || !this.selectedMarker) {
                    return;
                }

                this.isSavingMarker = true;

                try {
                    const result = await this.toggleSaved(this.selectedMarker.id);

                    if (result.success) {
                        const oldState = this.selectedMarker.ownershipState;
                        const newState = result.ownershipState;

                        // Update local modal state
                        this.selectedMarker.ownershipState = newState;

                        // Emit event to parent to update markers
                        this.$emit('listing-saved', {
                            listingId: this.selectedMarker.id,
                            oldState: oldState,
                            newState: newState
                        });

                        // Show success notification
                        const message = newState === 'Saved'
                            ? 'Listing saved successfully!'
                            : 'Listing removed from saved list';
                        this.showNotification(message, 'success');
                    } else {
                        throw new Error(result.error || 'Failed to update listing');
                    }
                } catch (error) {
                    console.error('Error toggling listing save:', error);
                    this.showNotification('Failed to update listing. Please try again.', 'error');
                } finally {
                    this.isSavingMarker = false;
                }
            }
        }
    }
</script>

<style scoped>
    .map-component {
        width: 100%;
    }

    .toolback {
        padding: 10px;
        background-color: white;
        border: 1px solid #ccc;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    }

    .listing-toggle-container {
        position: relative;
        display: inline-block;
    }

    .map-toggle-btn {
        /* Ensure map toggle buttons are styled consistently with card toggles */
    }

    .no-toggle {
        pointer-events: none;
        opacity: 0.6;
    }

    .debug-visible-markers {
        background: #f8f9fa;
        border: 1px solid #ddd;
        padding: 1rem;
        border-radius: 6px;
        font-size: 0.95em;
    }

        .debug-visible-markers ul {
            margin-bottom: 0;
        }

    /* Marker Detail Modal Overlay */
    .marker-detail-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100000;
        animation: fadeIn 0.2s ease-in;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }

    .marker-detail-card {
        background: white;
        padding: 1.5rem;
        border-radius: 12px;
        max-width: 400px;
        width: 90%;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        position: relative;
        animation: slideUp 0.3s ease-out;
    }

    @keyframes slideUp {
        from {
            transform: translateY(20px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .marker-detail-header h5 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #212529;
    }

    .marker-detail-header .text-muted {
        font-size: 0.875rem;
    }

    .marker-detail-actions {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-top: 0.5rem;
    }

    /* Full-width action buttons with text labels */
    .action-btn-full {
        width: 100%;
        padding: 0.75rem 1rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        font-weight: 500;
        border-radius: 8px;
        text-decoration: none;
        white-space: nowrap;
    }

    .action-btn-full:hover {
        transform: translateY(-2px);
        transition: transform 0.15s ease;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .action-btn-full i {
        flex-shrink: 0;
    }

    .action-btn-full span {
        flex-grow: 0;
    }

    .btn-close {
        position: absolute;
        top: 15px;
        right: 15px;
        background: transparent;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        opacity: 0.5;
        transition: opacity 0.2s;
    }

    .btn-close:hover {
        opacity: 1;
    }

    .btn-close::before {
        content: '×';
        display: block;
        font-size: 2rem;
        line-height: 1;
    }

    .btn-loading {
        opacity: 0.7;
        cursor: wait !important;
    }

    /* Mobile adjustments */
    @media (max-width: 576px) {
        .marker-detail-card {
            max-width: 100%;
            width: 95%;
            padding: 1.25rem;
        }

        .marker-detail-header h5 {
            font-size: 1.1rem;
            padding-right: 1.5rem; /* Space for close button */
        }
    }
</style>